interface LayoutItem {
  id: string;
  assetUrl: string;
  assetTitle: string;
  originalWidth?: number;
  originalHeight?: number;
  aspectRatio: number;
}

interface LayoutPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

// ✅ Add missing type definitions
export interface NoticeboardItem {
  id: string;
  assetUrl: string;
  assetTitle: string;
  aspectRatio: number;
}

export interface ItemPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface NoticeboardPage {
  items: Array<{
    item: NoticeboardItem;
    position: ItemPosition;
  }>;
  layout: "auto" | "grid" | "masonry";
}

export interface LayoutOptions {
  containerWidth: number;
  containerHeight: number;
  columns: number; // ✅ User-specified columns
  maxRowsPerPage?: number | null; // ✅ Optional: limit rows per page
  padding?: number;
}

/**
 * Smart Noticeboard Layout Engine
 * Uses bin-packing algorithm to efficiently arrange posters
 */
export class NoticeboardLayoutEngine {
  /**
   * Calculate layout with user-specified columns
   */
  static calculateLayout(
    items: NoticeboardItem[],
    options: LayoutOptions
  ): NoticeboardPage[] {
    const {
      containerWidth,
      containerHeight,
      columns = 4,
      maxRowsPerPage = null,
      padding = 20,
    } = options;

    if (items.length === 0) {
      return [];
    }

    // ✅ Calculate item width based on columns
    const availableWidth = containerWidth - padding * 2;
    const itemWidth = (availableWidth - padding * (columns - 1)) / columns;

    return this.calculateColumnBasedLayout(
      items,
      containerWidth,
      containerHeight,
      columns,
      itemWidth,
      maxRowsPerPage,
      padding
    );
  }

  /**
   * Column-based masonry layout
   */
  private static calculateColumnBasedLayout(
    items: NoticeboardItem[],
    containerWidth: number,
    containerHeight: number,
    columns: number,
    itemWidth: number,
    maxRowsPerPage: number | null,
    padding: number
  ): NoticeboardPage[] {
    const pages: NoticeboardPage[] = [];
    const availableHeight = containerHeight - padding * 2;

    // ✅ NEW: Calculate max item height to ensure items fit on screen
    const maxItemHeight = maxRowsPerPage
      ? (availableHeight - padding * (maxRowsPerPage - 1)) / maxRowsPerPage
      : availableHeight * 0.9; // Leave 10% buffer for centering

    // Track column heights for current page
    let columnHeights = new Array(columns).fill(0);
    let currentPageItems: Array<{
      item: NoticeboardItem;
      position: ItemPosition;
      calculatedY: number;
    }> = [];
    let itemIndex = 0;

    while (itemIndex < items.length) {
      const item = items[itemIndex];

      // ✅ FIXED: Calculate height from aspect ratio, but constrain to maxItemHeight
      let itemHeight = itemWidth / item.aspectRatio;

      // ✅ If item is too tall, scale it down proportionally
      if (itemHeight > maxItemHeight) {
        itemHeight = maxItemHeight;
      }

      // Find shortest column
      const shortestColumnIndex = columnHeights.indexOf(
        Math.min(...columnHeights)
      );
      const shortestColumnHeight = columnHeights[shortestColumnIndex];
      const itemBottom = shortestColumnHeight + itemHeight;

      // Check if we need a new page
      if (itemBottom > availableHeight && currentPageItems.length > 0) {
        // Create page
        const maxColumnHeight = Math.max(...columnHeights);
        const verticalOffset =
          padding + (availableHeight - maxColumnHeight) / 2;

        pages.push({
          items: currentPageItems.map(({ item, position, calculatedY }) => ({
            item,
            position: {
              ...position,
              y: calculatedY + verticalOffset,
            },
          })),
          layout: "masonry",
        });

        // Reset for new page
        currentPageItems = [];
        columnHeights = new Array(columns).fill(0);
        continue;
      }

      // Calculate X position
      const x = padding + shortestColumnIndex * (itemWidth + padding);

      // Add item to current page
      currentPageItems.push({
        item,
        position: {
          x,
          y: 0, // Will be set when centering
          width: itemWidth,
          height: itemHeight,
        },
        calculatedY: shortestColumnHeight,
      });

      // Update column height
      columnHeights[shortestColumnIndex] =
        shortestColumnHeight + itemHeight + padding;

      itemIndex++;
    }

    // Add final page
    if (currentPageItems.length > 0) {
      const maxColumnHeight = Math.max(...columnHeights);
      const verticalOffset = padding + (availableHeight - maxColumnHeight) / 2;

      pages.push({
        items: currentPageItems.map(({ item, position, calculatedY }) => ({
          item,
          position: {
            ...position,
            y: calculatedY + verticalOffset,
          },
        })),
        layout: "masonry",
      });
    }

    return pages;
  }
}
